// 📂 CurrencyDiagnosis.js
import React, { useState, useCallback, useMemo } from "react";
// import { useTranslation } from "react-i18next"; // ❌ useTranslation 제거
import Plot from "react-plotly.js"; // 차트 라이브러리
import { TrendingUp, Settings, BarChart2, Zap, AlertTriangle, HelpCircle } from "lucide-react"; // 아이콘
import { ToastContainer, toast } from 'react-toastify';
import DOMPurify from 'dompurify';

// ⚠️ 실제 환경에서는 경로가 다를 수 있으나, 일관성을 위해 commonStyles 경로를 유지합니다.
import commonStyles from "../../../styles/Common.module.css"; 
import styles from "./style/CorrPage.module.css"; 
// import 'react-toastify/dist/ReactToastify.css'; // Toastify CSS가 필요합니다.


// ========== 1. 스타일 및 헬퍼 함수 정의 (한글 텍스트 및 로직 유지) ==========

// 통화 코드 -> 한글 이름 매핑 (하드코딩)
const CURRENCY_NAMES = {
    'USD': '미국 달러',
    'KRW': '원',
    'EUR': '유로',
    'GBP': '영국 파운드', 
    'JPY': '일본 엔',
    'AUD': '호주 달러',
    'CAD': '캐나다 달러',
    'CHF': '스위스 프랑',
    'CNY': '위안화',
    'HKD': '홍콩 달러',
    'NZD': '뉴질랜드 달러',
};
const getCurrencyName = (code) => CURRENCY_NAMES[code] || code;

// 기간 매핑 (하드코딩)
const PERIOD_NAMES = {
    '6m': '6개월 (단기)',
    '1y': '1년 (표준)',
    '3y': '3년 (장기)',
};
const getPeriodName = (p) => PERIOD_NAMES[p] || p;

// 갭 비율 공식
const GAP_RATIO_FORMULA = String.raw`
    $$ \text{갭 비율} = \left( \frac{\text{대상 통화 강도 지수} (\text{C}_1 \text{ Index})}{\text{현재 환율} (\text{C}_1/\text{C}_2)} \right) \times 100 $$
    <p class="text-xs text-gray-600 mt-2">
    * C1 Index는 해당 통화의 펀더멘털 강도를 나타내는 지수(예: 달러 지수 DXY)입니다.
    </p>
`;

// 적정 환율 공식 (롤링 상관계수 기반)
const FAIR_VALUE_FORMULA = String.raw`
    $$ \text{적정 환율} = \frac{\text{현재 대상 통화 강도 지수} (\text{C}_1 \text{ Index}_{\text{Current}})}{\text{안정적 기간의 평균 비율} (\text{Ratio}_{\text{Stable}})} $$
    <p class="text-xs text-gray-600 mt-2">
    * DXY와 환율 간의 **롤링 상관계수가 임계치(${String.raw`\rho > 0.8`}) 이상**인 '안정적 기간'의 평균 비율($\text{Index} / \text{Rate}$)을 적정 환율 산출 기준으로 사용합니다.
    </p>
`;
const FORMULA_SOURCE_NOTE = '※ 본 공식은 특정 환율 진단 모델을 단순화하여 예시로 제공합니다.';


// 스타일 객체는 그대로 유지
const pageStyles = {
    controlPanel: "md:col-span-1 bg-white p-6 rounded-xl shadow-lg h-fit sticky top-4 space-y-4", 
    contentArea: "md:col-span-3 space-y-8",
    card: "bg-white p-6 rounded-xl shadow-lg border border-gray-100",
    sectionTitle: "text-xl font-semibold mb-4 text-gray-700 flex items-center gap-2 border-b pb-2",
    buttonPrimary: styles["alloc-primary-button"], 
    buttonSecondary: styles["alloc-secondary-button"],
    selectInput: "mt-1 p-2 border border-gray-300 rounded-lg focus:ring-indigo-500 focus:border-indigo-500 w-full",
    dataGrid: "grid grid-cols-2 sm:grid-cols-4 gap-4",
    dataBox: "p-4 bg-gray-50 border border-gray-200 rounded-lg text-center",
    dataLabel: "text-sm font-medium text-gray-500",
    dataValue: "text-xl font-bold mt-1 break-words",
    chartScroll: "overflow-x-auto w-full",
    chartContainer: "min-w-[650px] w-full h-[400px]",
    formulaContainer: "mt-4 p-4 bg-indigo-50 border-l-4 border-indigo-500 rounded-lg shadow-inner",
    loadingOverlay: styles.loadingOverlay,
    doubleBounce: styles.doubleBounce,
    doubleBounce1: styles.doubleBounce1,
    doubleBounce2: styles.doubleBounce2,
};

// Numeric Format 헬퍼 함수 (유지)
const formatNumber = (value, options = {}) => {
    if (value === null || value === undefined || isNaN(value)) return "N/A";

    const {
        prefix = "",
        suffix = "",
        decimalScale = 2,
        isPercentage = false,
        useGrouping = true
    } = options;

    const formatterOptions = {
        style: 'decimal',
        minimumFractionDigits: decimalScale,
        maximumFractionDigits: decimalScale,
        useGrouping: useGrouping
    };

    let formattedValue;
    try {
        formattedValue = new Intl.NumberFormat('en-US', formatterOptions).format(value);
    } catch (e) {
        formattedValue = value.toFixed(decimalScale);
    }

    const finalSuffix = isPercentage ? '%' : suffix;

    return `${prefix}${formattedValue}${finalSuffix}`;
};

// MathJax를 사용하여 LaTeX를 렌더링하는 함수 (유지)
const renderLatex = (formula) => {
    const sanitizedFormula = DOMPurify.sanitize(formula, { USE_PROFILES: { html: false }});
    return (
        <p
            className="overflow-x-auto text-sm my-2 p-2 bg-white rounded-md border border-gray-200 text-gray-800"
            dangerouslySetInnerHTML={{ __html: sanitizedFormula }}
        />
    );
};

// ========== 2. 데이터 정의 (제한된 통화 목록) ==========

// C1은 USD로 고정하고, C2 옵션만 정의
const CURRENCIES_C2_OPTIONS = [
    'KRW', 
    'EUR', 
    'GBP', 
    'JPY', 
    'AUD', 
    'CAD', 
    'CHF', 
    'CNY', 
    'HKD', 
    'NZD'
];
const PERIODS = Object.keys(PERIOD_NAMES);


// ========== 3. Main Component (Netlify API 연동) ==========

function CurrencyDiagnosisPage() {
    const [c1, setC1] = useState('USD'); 
    const [c2, setC2] = useState('KRW'); 
    const [period, setPeriod] = useState('1y');
    const [diagnosisResult, setDiagnosisResult] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);

    // 차트 공통 설정
    const chartConfig = {
        displayModeBar: false, // 차트 툴바 숨기기
        responsive: true
    };


    // 통화 선택 옵션 (한글 이름 적용)
    const currencyOptionsC2 = useMemo(() => {
        return CURRENCIES_C2_OPTIONS.map(code => ({
            code,
            name: getCurrencyName(code)
        }));
    }, []);

    // Netlify Function을 호출하는 진단 실행 함수 (로직 유지)
    const handleRunDiagnosis = useCallback(async () => {
        if (c1 === c2) {
            toast.error('분석 대상 통화와 베이스 통화는 같을 수 없습니다.');
            setDiagnosisResult(null);
            return;
        }

        setError(null);
        setIsLoading(true);
        setDiagnosisResult(null);

        try {
            // Netlify Function 호출 경로: /api/diagnosis
            const response = await fetch(`/.netlify/functions/cur_diagnosis?c1=${c1}&c2=${c2}&period=${period}`);
            
            if (!response.ok) {
                const errorData = await response.json();
                // 서버의 상세 오류 메시지 사용
                throw new Error(errorData.detail || errorData.error || `Netlify Function 오류 (${response.status})`);
            }

            const result = await response.json();
            
            if (!result || !result.exchangeRate || !result.historicalRates || !result.historicalIndexRates) {
                 throw new Error("서버에서 유효하지 않은 진단 결과를 받았습니다. (필수 데이터 누락)");
            }
            
            setDiagnosisResult(result);

        } catch (e) {
            console.error("Diagnosis failed:", e);
            toast.error(`환율 진단 실행에 실패했습니다: ${e.message || '알 수 없는 네트워크 오류'}`);
        } finally {
            setIsLoading(false);
        }
    }, [c1, c2, period]);
    
    // 1. 환율 추이 차트 데이터 Configuration
    const rateChartData = useMemo(() => {
        if (!diagnosisResult || !diagnosisResult.historicalRates || diagnosisResult.historicalRates.length === 0) return [];

        const baseData = [{
            x: diagnosisResult.dates,
            y: diagnosisResult.historicalRates,
            type: 'scatter',
            mode: 'lines',
            name: `${getCurrencyName(c1)} / ${getCurrencyName(c2)} 환율`, 
            line: { color: '#059669', width: 2 },
        }];

        // 롤링으로 계산된 적정 환율 추이(historicalFairValues)를 동적인 선으로 추가
        if (diagnosisResult.historicalFairValues && diagnosisResult.historicalFairValues.length > 0) {
            baseData.push({
                x: diagnosisResult.dates,
                y: diagnosisResult.historicalFairValues,
                type: 'scatter',
                mode: 'lines',
                name: '적정 환율 추이 (상관계수 기반)', 
                line: { color: '#f97316', width: 2, dash: 'dashdot' }, // 주황색 점선으로 표시
            });
        }
        
        // 환율의 롤링 중간값 추이 (키 이름 및 윈도우 크기 반영)
        if (diagnosisResult.historicalRollingMedian && diagnosisResult.historicalRollingMedian.length > 0) {
             baseData.push({
                x: diagnosisResult.dates,
                y: diagnosisResult.historicalRollingMedian, 
                type: 'scatter',
                mode: 'lines',
                // 윈도우 크기를 동적으로 표시
                name: `환율 롤링 중간값 추이 (${diagnosisResult.medianWindowSize}일)`, 
                line: { color: '#3b82f6', width: 1.5, dash: 'dot' }, 
            });
        }
        
        return baseData;
    }, [diagnosisResult, c1, c2]);

    // 2. 환율 추이 차트 Layout Configuration (유지)
    const rateChartLayout = useMemo(() => {
        const c1Name = getCurrencyName(c1);
        const c2Name = getCurrencyName(c2);
        
        return {
            title: {
                text: `환율 추이 (${c1Name} / ${c2Name})`, 
                font: { size: 16, color: '#1f2937' }
            },
            xaxis: {
                title: '시간', 
                automargin: true,
                showgrid: true,
                gridcolor: '#e5e7eb',
                tickangle: -45,
            },
            yaxis: {
                title: `환율 (${c2Name} 단위)`, 
                automargin: true,
                showgrid: true,
                gridcolor: '#e5e7eb',
            },
            height: 400,
            autosize: true,
            margin: { t: 40, r: 20, b: 80, l: 60 },
            hovermode: 'x unified',
            paper_bgcolor: '#ffffff',
            plot_bgcolor: '#ffffff',
            
            shapes: [],
            annotations: []
        };
    }, [diagnosisResult, c1, c2]);
    
    // 3. DXY 추이 차트 데이터 Configuration
    const dxyChartData = useMemo(() => {
        if (!diagnosisResult || !diagnosisResult.historicalIndexRates || diagnosisResult.historicalIndexRates.length === 0) return [];

        const baseData = [{
            x: diagnosisResult.dates,
            y: diagnosisResult.historicalIndexRates,
            type: 'scatter',
            mode: 'lines',
            name: diagnosisResult.indexValue.name, 
            line: { color: '#0f395e', width: 2 }, 
        }];
        
        // 펀더멘털 지표(DXY)의 롤링 중간값 추이 (키 이름 및 윈도우 크기 반영)
        if (diagnosisResult.historicalIndexRollingMedian && diagnosisResult.historicalIndexRollingMedian.length > 0) {
            baseData.push({
                x: diagnosisResult.dates,
                y: diagnosisResult.historicalIndexRollingMedian, 
                type: 'scatter',
                mode: 'lines',
                // 윈도우 크기를 동적으로 표시
                name: `${diagnosisResult.indexValue.name} 롤링 중간값 추이 (${diagnosisResult.medianWindowSize}일)`, 
                line: { color: '#f59e0b', width: 1.5, dash: 'dot' }, 
            });
        }
        
        return baseData;

    }, [diagnosisResult]);

    // 4. DXY 추이 차트 Layout Configuration (유지)
    const dxyChartLayout = useMemo(() => {
        const indexName = diagnosisResult?.indexValue.name || 'USD Index';

        return {
            title: {
                text: `${indexName} 추이 (${getPeriodName(period)})`, 
                font: { size: 16, color: '#1f2937' }
            },
            xaxis: {
                title: '시간', 
                automargin: true,
                showgrid: true,
                gridcolor: '#e5e7eb',
                tickangle: -45,
            },
            yaxis: {
                title: `${indexName} 값`, 
                automargin: true,
                showgrid: true,
                gridcolor: '#e5e7eb',
            },
            height: 400,
            autosize: true,
            margin: { t: 40, r: 20, b: 80, l: 60 },
            hovermode: 'x unified',
            paper_bgcolor: '#ffffff',
            plot_bgcolor: '#ffffff',
            
            shapes: [], 
            annotations: []
        };
    }, [diagnosisResult, period]);

    // 5. 갭 비율 추이 차트 데이터 Configuration
    const ratioChartData = useMemo(() => {
        if (!diagnosisResult || !diagnosisResult.historicalGapRatios || diagnosisResult.historicalGapRatios.length === 0) return [];
        
        const historicalGapRatios = diagnosisResult.historicalGapRatios;
        const historicalCorrelations = diagnosisResult.historicalCorrelations || [];
        const threshold = diagnosisResult.rollingCorrModel?.correlationThreshold || 0.8; 
        const ratioStableValue = diagnosisResult.rollingCorrModel?.stableRatio / 100; // 비율(%) -> 비율(소수점)

        // 안정적 상관관계 기간의 갭 비율만 추출
        const stableGapRatios = historicalGapRatios.map((ratio, i) => {
            const corr = historicalCorrelations[i];
            if (corr !== null && ratio !== null && Math.abs(corr) > threshold) {
                return ratio;
            }
            return null;
        });

        const baseData = [{
            x: diagnosisResult.dates,
            y: historicalGapRatios,
            type: 'scatter',
            mode: 'lines',
            name: '갭 비율 추이 (Index/Rate * 100)', 
            line: { color: '#9ca3af', width: 1.5 }, // 회색 라인
        }];

        // Trace 2: 안정적 구간 하이라이트
        if (stableGapRatios.some(v => v !== null) && historicalCorrelations.length > 0) {
             baseData.push({
                x: diagnosisResult.dates,
                y: stableGapRatios,
                type: 'scatter',
                mode: 'lines',
                name: `안정적 상관관계 기간 (ρ > ${threshold})`, 
                line: { color: '#ef4444', width: 4 }, // 붉은색 강조 라인
            });
        }
        
        // Trace 3: 갭 비율 롤링 중간값 선 (키 이름 및 윈도우 크기 반영)
        if (diagnosisResult.historicalGapRatioRollingMedian && diagnosisResult.historicalGapRatioRollingMedian.length > 0) {
            baseData.push({
                x: diagnosisResult.dates,
                y: diagnosisResult.historicalGapRatioRollingMedian,
                type: 'scatter',
                mode: 'lines',
                // 윈도우 크기를 동적으로 표시
                name: `갭 비율 롤링 중간값 추이 (${diagnosisResult.medianWindowSize}일)`, 
                line: { color: '#3b82f6', width: 1.5, dash: 'dot' }, 
            });
        }
        
        // Trace 4: 안정적 비율 평균값 (Fair Value 기준)
        if (ratioStableValue !== null && ratioStableValue !== undefined && !isNaN(ratioStableValue)) {
            baseData.push({
                x: diagnosisResult.dates,
                y: Array(diagnosisResult.dates.length).fill(ratioStableValue * 100), // 비율(소수점)을 비율(%)로 다시 변환
                type: 'scatter',
                mode: 'lines',
                name: '안정적 기간 평균 비율 (Fair Value 기준)',
                line: { color: '#000000', width: 2, dash: 'solid' }, // 검은색 실선
            });
        }
        
        return baseData;

    }, [diagnosisResult]);
    
    // 6. 갭 비율 추이 차트 Layout Configuration
    const ratioChartLayout = useMemo(() => {
        const c1Name = getCurrencyName(c1);
        const c2Name = getCurrencyName(c2);
        const ratioStableValue = diagnosisResult?.rollingCorrModel?.stableRatio; // 비율(%)

        // --- Annotation for Ratio_Stable ---
        let annotations = [];
        // **[핵심]** ratioStableValue가 유효한 경우, 정상 주석 추가
        if (ratioStableValue !== null && ratioStableValue !== undefined && !isNaN(ratioStableValue)) {
            // 1) 정상적인 수평선 값 주석 (차트 우측에 표시)
            annotations.push({
                xref: 'paper', yref: 'y', x: 1.0, // 오른쪽 끝
                y: ratioStableValue,
                text: `안정 평균: ${formatNumber(ratioStableValue, { decimalScale: 2 })}`,
                showarrow: false, xanchor: 'right', yanchor: 'middle', ax: 0, ay: 0,
                bgcolor: 'rgba(255, 255, 255, 0.9)', bordercolor: '#000000', borderwidth: 1,
                font: { size: 10, color: '#000000' }
            });
        } else if (diagnosisResult) {
            // 2) 🚨 디버깅 경고 주석 (값이 유효하지 않을 경우 차트 상단 중앙에 표시)
            annotations.push({
                xref: 'paper', yref: 'paper', x: 0.5, y: 1.05,
                text: '※ 안정적 상관관계 기간 부족으로 적정 환율 산출이 어렵습니다.',
                showarrow: false, xanchor: 'center', yanchor: 'bottom',
                bgcolor: 'rgba(254, 226, 226, 0.9)', bordercolor: '#ef4444', borderwidth: 1,
                font: { size: 10, color: '#b91c1c' }
            });
        }

        return {
            title: {
                text: `갭 비율 추이 (안정적 상관관계 기간 하이라이트) - ${c1}/${c2}`, 
                font: { size: 16, color: '#1f2937' }
            },
            xaxis: {
                title: '시간', 
                automargin: true,
                showgrid: true,
                gridcolor: '#e5e7eb',
                tickangle: -45,
            },
            yaxis: {
                title: '갭 비율 (Index/Rate * 100)', 
                automargin: true,
                showgrid: true,
                gridcolor: '#e5e7eb',
            },
            height: 400,
            autosize: true,
            margin: { t: 40, r: 120, b: 80, l: 60 }, // 오른쪽 마진을 넓게 설정
            hovermode: 'x unified',
            paper_bgcolor: '#ffffff',
            plot_bgcolor: '#ffffff',
            
            shapes: [], 
            annotations: annotations
        };
    }, [diagnosisResult, c1, c2]);

    // 7. 롤링 상관계수 추이 차트 데이터 Configuration (유지)
    const corrChartData = useMemo(() => {
        if (!diagnosisResult || !diagnosisResult.historicalCorrelations || diagnosisResult.historicalCorrelations.length === 0) return [];
        
        const baseData = [{
            x: diagnosisResult.dates,
            y: diagnosisResult.historicalCorrelations,
            type: 'scatter',
            mode: 'lines',
            name: '롤링 상관계수 추이', 
            line: { color: '#ef4444', width: 2 }, // 빨간색 계열
        }];

        // 안정적 기간 임계치 (Threshold) 라인 추가
        const threshold = diagnosisResult.rollingCorrModel?.correlationThreshold || 0.8;
        
        baseData.push({
            x: diagnosisResult.dates,
            y: Array(diagnosisResult.dates.length).fill(threshold),
            type: 'scatter',
            mode: 'lines',
            name: '안정적 임계치 (+)',
            line: { color: '#059669', width: 1, dash: 'dash' },
        });
        
        baseData.push({
            x: diagnosisResult.dates,
            y: Array(diagnosisResult.dates.length).fill(-threshold),
            type: 'scatter',
            mode: 'lines',
            name: '안정적 임계치 (-)',
            line: { color: '#059669', width: 1, dash: 'dash' },
        });

        return baseData;
    }, [diagnosisResult]);

    // 8. 롤링 상관계수 차트 Layout Configuration (유지)
    const corrChartLayout = useMemo(() => {
        const c1Name = getCurrencyName(c1);
        const c2Name = getCurrencyName(c2);

        return {
            title: {
                text: `${c1Name}/${c2Name} 환율 vs DXY 롤링 상관계수 추이`, 
                font: { size: 16, color: '#1f2937' }
            },
            xaxis: {
                title: '시간', 
                automargin: true,
                showgrid: true,
                gridcolor: '#e5e7eb',
                tickangle: -45,
            },
            yaxis: {
                title: '상관계수 (Pearson ρ)', 
                automargin: true,
                showgrid: true,
                gridcolor: '#e5e7eb',
                range: [-1, 1], // 상관계수 범위 고정
            },
            height: 400,
            autosize: true,
            margin: { t: 40, r: 20, b: 80, l: 60 },
            hovermode: 'x unified',
            paper_bgcolor: '#ffffff',
            plot_bgcolor: '#ffffff',
            
            shapes: [ // 0% 기준선 추가
                { type: 'line', xref: 'paper', yref: 'y', x0: 0, y0: 0, x1: 1, y1: 0, line: { color: '#9ca3af', width: 1, dash: 'dot' } }
            ], 
            annotations: []
        };
    }, [c1, c2]);

    // 데이터 없음 메시지
    const noDataMessage = (
        <div className={pageStyles.card + " text-center py-20"}>
            <AlertTriangle size={48} className="text-yellow-500 mx-auto mb-4" />
            <p className="text-xl font-semibold text-gray-700">진단 결과가 없습니다.</p>
            <p className="text-gray-500 mt-2">
                분석할 통화 쌍과 기간을 선택하신 후, <span className="font-bold">"환율 진단 실행"</span> 버튼을 눌러주세요.
            </p>
        </div>
    );
    
    // 로딩 오버레이
    const loadingOverlay = (
        <div className={pageStyles.loadingOverlay}>
            <div className={pageStyles.doubleBounce}>
                <div className={pageStyles.doubleBounce1}></div>
                <div className={pageStyles.doubleBounce2}></div>
            </div>
            <p className="mt-4 text-white font-semibold">데이터를 가져오고 진단을 실행 중입니다...</p>
        </div>
    );

    // 최종 렌더링
    return (
        <div className="min-h-screen bg-gray-50 p-4 sm:p-8">
            <main className="max-w-7xl mx-auto">
                <header className="text-center mb-10">
                    <h1 className="text-3xl font-extrabold text-gray-900 flex items-center justify-center gap-2">
                        <BarChart2 size={30} className="text-indigo-600" /> 통화 펀더멘털 적정 환율 진단
                    </h1>
                    <p className="mt-2 text-gray-600">
                        선택된 통화 강도 지수와 환율 간의 상관관계를 분석하여 적정 환율을 추정합니다.
                    </p>
                </header>

                {isLoading && loadingOverlay}

                <div className="grid gap-6 md:grid-cols-4 max-w-7xl mx-auto">
                    {/* A. 진단 설정 패널 */}
                    <div className={pageStyles.controlPanel}>
                        <h2 className={pageStyles.sectionTitle}>
                            <Settings size={20} className="text-indigo-600" /> 진단 설정
                        </h2>
                        <div className="space-y-4">
                            {/* 1. Target Currency (C1) - USD로 고정 */}
                            <div className="flex flex-col">
                                <label htmlFor="c1" className={pageStyles.dataLabel}>
                                    분석 대상 통화 (C1)
                                </label>
                                <div className="mt-1 p-2 bg-gray-100 border border-gray-300 rounded-lg w-full font-bold text-gray-700">
                                    {getCurrencyName(c1)} ({c1}) <span className="text-xs text-gray-500">(고정: DXY 기반)</span>
                                </div>
                            </div>
                            {/* 2. Base Currency (C2) */}
                            <div className="flex flex-col">
                                <label htmlFor="c2" className={pageStyles.dataLabel}>
                                    기본 베이스 통화 (C2) - 결과 단위
                                </label>
                                <select 
                                    id="c2" 
                                    value={c2} 
                                    onChange={(e) => setC2(e.target.value)} 
                                    className={pageStyles.selectInput}
                                >
                                    {currencyOptionsC2.map(opt => (
                                        <option key={opt.code} value={opt.code}>
                                            {opt.name} ({opt.code})
                                        </option>
                                    ))}
                                </select>
                            </div>
                            {/* 3. Reference Period */}
                            <div className="flex flex-col">
                                <label htmlFor="period" className={pageStyles.dataLabel}>
                                    기준 기간
                                </label>
                                <select 
                                    id="period" 
                                    value={period} 
                                    onChange={(e) => setPeriod(e.target.value)} 
                                    className={pageStyles.selectInput}
                                >
                                    {PERIODS.map(p => (
                                        <option key={p} value={p}>
                                            {getPeriodName(p)}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            {/* 진단 실행 버튼 */}
                            <button 
                                onClick={handleRunDiagnosis} 
                                disabled={isLoading}
                                className={pageStyles.buttonPrimary + (isLoading ? ' opacity-50 cursor-not-allowed' : '')}
                            >
                                {isLoading ? '진단 실행 중...' : '환율 진단 실행'}
                            </button>
                        </div>
                    </div>

                    {/* B & C. 결과 및 차트 영역 */}
                    <div className={pageStyles.contentArea}>
                        {diagnosisResult ? (
                            <>
                                {/* B. 현재 시장 요약 및 진단 결과 */}
                                <div className={pageStyles.card}>
                                    <h2 className={pageStyles.sectionTitle}>
                                        <Zap size={20} className="text-red-600" /> 현재 시장 요약 진단
                                    </h2>

                                    <div className={pageStyles.dataGrid}>
                                        {/* Current Rate */}
                                        <div className={pageStyles.dataBox}>
                                            <p className={pageStyles.dataLabel}>현재 환율</p>
                                            <p className={pageStyles.dataValue}>
                                                {formatNumber(diagnosisResult.exchangeRate.current, { decimalScale: 4 })}
                                            </p>
                                        </div>
                                        
                                        {/* 전체 기간 중간값 */}
                                        <div className={pageStyles.dataBox}>
                                            <p className={pageStyles.dataLabel}>전체 기간 중간값</p>
                                            <p className={pageStyles.dataValue}>
                                                {formatNumber(diagnosisResult.exchangeRate.medianFullPeriod, { decimalScale: 4 })} 
                                            </p>
                                        </div>
                                        
                                        {/* Strength Index */}
                                        <div className={pageStyles.dataBox}>
                                            <p className={pageStyles.dataLabel}>{diagnosisResult.indexValue.name} (괴리율)</p>
                                            <p className={pageStyles.dataValue + (diagnosisResult.indexValue.gap > 0 ? ' text-red-600' : ' text-green-600')}> 
                                                {formatNumber(diagnosisResult.indexValue.gap, { decimalScale: 2, suffix: '%', prefix: diagnosisResult.indexValue.gap > 0 ? '+' : '' })}
                                            </p>
                                        </div>
                                        
                                        {/* Gap Ratio */}
                                        <div className={pageStyles.dataBox}>
                                            <p className={pageStyles.dataLabel}>현재 갭 비율</p>
                                            <p className={pageStyles.dataValue}>
                                                {formatNumber(diagnosisResult.gapRatio.current, { decimalScale: 2 })}
                                            </p>
                                        </div>

                                        {/* Fair Value */}
                                        <div className={pageStyles.dataBox}>
                                            <p className={pageStyles.dataLabel}>적정 환율 (Fair Value)</p>
                                            <p className={pageStyles.dataValue}>
                                                {formatNumber(diagnosisResult.fairValue.value, { decimalScale: 4 })}
                                            </p>
                                        </div>
                                        
                                        {/* Gap from Current */}
                                        <div className={pageStyles.dataBox}>
                                            <p className={pageStyles.dataLabel}>Fair Value 대비 괴리율</p>
                                            {/* 괴리율 양수: 현재 환율이 FV보다 높음 (과대평가), 음수: 과소평가 */}
                                            <p className={pageStyles.dataValue + (diagnosisResult.fairValue.gapFromCurrent > 0 ? ' text-red-600' : ' text-green-600')}>
                                                {formatNumber(diagnosisResult.fairValue.gapFromCurrent, { decimalScale: 2, suffix: '%', prefix: diagnosisResult.fairValue.gapFromCurrent > 0 ? '+' : '' })}
                                            </p>
                                        </div>
                                        
                                        {/* Correlation */}
                                        <div className={pageStyles.dataBox}>
                                            <p className={pageStyles.dataLabel}>현재 롤링 상관계수</p>
                                            <p className={pageStyles.dataValue}>
                                                {formatNumber(diagnosisResult.rollingCorrModel.currentCorrelation, { decimalScale: 3 })}
                                            </p>
                                        </div>

                                        {/* Stable Count */}
                                        <div className={pageStyles.dataBox}>
                                            <p className={pageStyles.dataLabel}>안정 기간 확보일 (목표: 90일+)</p>
                                            <p className={pageStyles.dataValue}>
                                                {diagnosisResult.rollingCorrModel.stablePeriodCount} 일
                                            </p>
                                        </div>
                                    </div>
                                </div>
                                
                                {/* C. 환율 추이 차트 (Actual Rate) - 롤링 적정 환율 선 및 롤링 중간값 라인 포함 */}
                                <div className={pageStyles.card}>
                                    <h2 className={pageStyles.sectionTitle}>
                                        <TrendingUp size={20} className="text-blue-600" /> 환율 추이 차트 (Actual Rate & Fair Value Trend)
                                    </h2>
                                    <div className={pageStyles.chartScroll}>
                                        <div className={pageStyles.chartContainer}>
                                            <Plot
                                                data={rateChartData}
                                                layout={rateChartLayout}
                                                config={chartConfig}
                                                useResizeHandler={true}
                                                style={{ width: "100%", height: "100%" }}
                                            />
                                        </div>
                                    </div>
                                </div>

                                {/* D. 펀더멘털 지수 (DXY) 추이 차트 */}
                                <div className={pageStyles.card}>
                                    <h2 className={pageStyles.sectionTitle}>
                                        <TrendingUp size={20} className="text-yellow-600" /> {diagnosisResult.indexValue.name} 추이
                                    </h2>
                                    <div className={pageStyles.chartScroll}>
                                        <div className={pageStyles.chartContainer}>
                                            <Plot
                                                data={dxyChartData}
                                                layout={dxyChartLayout}
                                                config={chartConfig}
                                                useResizeHandler={true}
                                                style={{ width: "100%", height: "100%" }}
                                            />
                                        </div>
                                    </div>
                                </div>

                                {/* E. 갭 비율 추이 차트 (핵심) */}
                                <div className={pageStyles.card}>
                                    <h2 className={pageStyles.sectionTitle}>
                                        <BarChart2 size={20} className="text-red-600" /> 갭 비율 추이 (안정적 상관관계 기간 하이라이트)
                                    </h2>
                                    <div className={pageStyles.chartScroll}>
                                        <div className={pageStyles.chartContainer}>
                                            <Plot
                                                data={ratioChartData}
                                                layout={ratioChartLayout}
                                                config={chartConfig}
                                                useResizeHandler={true}
                                                style={{ width: "100%", height: "100%" }}
                                            />
                                        </div>
                                    </div>
                                </div>

                                {/* F. 롤링 상관계수 추이 차트 */}
                                <div className={pageStyles.card}>
                                    <h2 className={pageStyles.sectionTitle}>
                                        <BarChart2 size={20} className="text-green-600" /> 롤링 상관계수 추이
                                    </h2>
                                    <div className={pageStyles.chartScroll}>
                                        <div className={pageStyles.chartContainer}>
                                            <Plot
                                                data={corrChartData}
                                                layout={corrChartLayout}
                                                config={chartConfig}
                                                useResizeHandler={true}
                                                style={{ width: "100%", height: "100%" }}
                                            />
                                        </div>
                                    </div>
                                </div>

                                {/* G. 진단 공식 설명 */}
                                <div className={pageStyles.card}>
                                    <h2 className={pageStyles.sectionTitle}>
                                        <HelpCircle size={20} className="text-indigo-600" /> 진단 모델 및 공식
                                    </h2>
                                    <div className={pageStyles.formulaContainer}>
                                        <h3 className="text-lg font-bold text-indigo-700">갭 비율 (Gap Ratio)</h3>
                                        {renderLatex(GAP_RATIO_FORMULA)}
                                        
                                        <h3 className="text-lg font-bold text-indigo-700 mt-4">적정 환율 (Fair Value)</h3>
                                        {renderLatex(FAIR_VALUE_FORMULA)}

                                        <p className="text-xs mt-4 text-indigo-800 italic">
                                            {FORMULA_SOURCE_NOTE}
                                        </p>
                                    </div>
                                </div>
                            </>
                        ) : (
                            // 데이터 없음
                            noDataMessage
                        )}
                    </div>
                </div>
            </main>
            
            {/* ToastContainer 유지 */}
            <ToastContainer
                autoClose={5000}
                hideProgressBar={false}
                newestOnTop={false}
                closeOnClick
                rtl={false}
                pauseOnFocusLoss
                draggable
                pauseOnHover
                style={{
                    position: 'fixed',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    zIndex: 9999,
                    border: 'none', 
                }}
            />
        </div>
    );
}

export default CurrencyDiagnosisPage;